import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, CircularProgress, Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { hr, fmtTime, ROLE_LABELS } from "./api";
import { Avatar, Card, Empty } from "./shared";
import {
  MyOverview,
  MyAttendance,
  MyLeave,
  MyDocuments,
  MyRequests,
  MyProfile,
  ApplyLeaveDialog,
  RegularizeDialog,
  AskHrDialog,
  useCheckInOut,
} from "./Me";
import { Approvals, TeamView, HrHome } from "./Team";
import { Employees } from "./Employees";
import { LeaveAdmin, HrDesk, Reports, Settings } from "./HrAdmin";
import styles from "./Hrms.module.css";

// Every tab, with the screen it renders.
const TABS = {
  overview: { label: "Overview", icon: <HomeOutlinedIcon />, Component: MyOverview },
  attendance: { label: "Attendance", icon: <TodayOutlinedIcon />, Component: MyAttendance },
  leave: { label: "Leave", icon: <BeachAccessOutlinedIcon />, Component: MyLeave },
  documents: { label: "Payslips", icon: <ReceiptLongOutlinedIcon />, Component: MyDocuments },
  requests: { label: "Ask HR", icon: <SupportAgentOutlinedIcon />, Component: MyRequests },
  profile: { label: "Profile", icon: <PersonOutlineIcon />, Component: MyProfile },
  hr_home: { label: "Dashboard", icon: <DashboardOutlinedIcon />, Component: HrHome },
  approvals: { label: "Approvals", icon: <FactCheckOutlinedIcon />, Component: Approvals },
  employees: { label: "Employees", icon: <GroupsOutlinedIcon />, Component: Employees },
  team: { label: "Attendance", icon: <TodayOutlinedIcon />, Component: TeamView },
  leave_admin: { label: "Leave", icon: <EventNoteOutlinedIcon />, Component: LeaveAdmin },
  desk: { label: "Help desk", icon: <SupportAgentOutlinedIcon />, Component: HrDesk },
  reports: { label: "Reports", icon: <InsightsOutlinedIcon />, Component: Reports },
  settings: { label: "Settings", icon: <SettingsOutlinedIcon />, Component: Settings },
};

// The two sides of the toggle. Which a person gets follows their HRMS role
// (services/hrms/access.js): anyone with an employee record has "My HR";
// managers get "My team"; HR and HRMS admins get "HR".
function modesFor(me) {
  const modes = [];
  if (me.employee) {
    modes.push({ key: "me", label: "My HR", icon: <PersonOutlineIcon />, tabs: ["overview", "attendance", "leave", "documents", "requests", "profile"] });
  }
  if (me.isHr) {
    const tabs = ["hr_home", "approvals", "employees", "team", "leave_admin", "desk", "reports", "settings"];
    modes.push({ key: "hr", label: "HR", icon: <BadgeOutlinedIcon />, tabs });
  } else if (me.isManager) {
    modes.push({ key: "hr", label: "My team", icon: <GroupsOutlinedIcon />, tabs: ["approvals", "team"] });
  }
  return modes;
}

const badgeFor = (key, me) => (key === "approvals" ? me.pendingApprovals : key === "desk" ? me.openHrRequests : 0);

function greeting(timeZone) {
  const h = Number(new Date().toLocaleString("en-GB", { hour: "2-digit", hour12: false, timeZone }));
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

// Top-right "Quick actions": the everyday things, one click from any tab.
function QuickActions({ me, mode, goTo, openDialog, onChanged }) {
  const [anchor, setAnchor] = useState(null);
  const { state, act } = useCheckInOut(me, onChanged);
  const close = () => setAnchor(null);
  const pick = (fn) => () => {
    close();
    fn();
  };
  const item = (key, icon, color, label, fn, extra) => (
    <MenuItem key={key} onClick={pick(fn)} sx={{ py: 1, px: 2, gap: 0.5, minWidth: 260, fontFamily: "inherit" }}>
      <ListItemIcon sx={{ color, minWidth: "34px !important" }}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14, fontFamily: "inherit" }} />
      {extra ? <span className={styles.badge}>{extra}</span> : null}
    </MenuItem>
  );
  const head = (key, text) => (
    <ListSubheader key={key} sx={{ lineHeight: "32px", fontWeight: 800, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>
      {text}
    </ListSubheader>
  );
  const mine = me.employee
    ? [
        head("h-me", "Me"),
        state === "out" && item("in", <LoginRoundedIcon />, "#16a34a", "Check in now", act),
        state === "in" && item("out", <LogoutRoundedIcon />, "#dc2626", "Check out now", act),
        item("leave", <BeachAccessOutlinedIcon />, "#2563eb", "Apply for leave", () => openDialog("leave")),
        item("fix", <EditCalendarOutlinedIcon />, "#d97706", "Fix a missed check-in", () => openDialog("regularize")),
        item("ask", <SupportAgentOutlinedIcon />, "#0d9488", "Ask HR", () => openDialog("ask")),
        item("slips", <ReceiptLongOutlinedIcon />, "#16a34a", "My payslips", () => goTo("documents")),
      ].filter(Boolean)
    : [];
  const team =
    me.isHr || me.isManager
      ? [
          head("h-hr", me.isHr ? "HR" : "My team"),
          item("appr", <FactCheckOutlinedIcon />, "#dc2626", "Review approvals", () => goTo("approvals"), me.pendingApprovals),
          me.isHr && item("add", <PersonAddAlt1OutlinedIcon />, "#7c3aed", "Add employee", () => goTo("employees", { new: "1" })),
          me.isHr && item("desk", <SupportAgentOutlinedIcon />, "#0d9488", "Help desk", () => goTo("desk"), me.openHrRequests),
          me.isHr && item("hol", <CelebrationOutlinedIcon />, "#db2777", "Add a holiday", () => goTo("leave_admin", { view: "holidays" })),
          item("att", <TodayOutlinedIcon />, "#2563eb", "Team attendance", () => goTo("team")),
          me.isHr && item("rep", <InsightsOutlinedIcon />, "#7c3aed", "Reports", () => goTo("reports")),
        ].filter(Boolean)
      : [];
  const groups = mode === "hr" ? [team, mine] : [mine, team];
  const items = groups.filter((g) => g.length).flatMap((g, i) => (i ? [<Divider key={`d${i}`} />, ...g] : g));
  const count = (me.pendingApprovals || 0) + (me.isHr ? me.openHrRequests || 0 : 0);
  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<BoltRoundedIcon />}
        endIcon={<KeyboardArrowDownRoundedIcon />}
        aria-haspopup="menu"
        sx={{
          background: "#fff",
          color: "var(--accent)",
          textTransform: "none",
          fontWeight: 800,
          fontSize: 14,
          fontFamily: "inherit",
          borderRadius: "999px",
          px: 2.25,
          py: 1,
          boxShadow: "0 4px 14px rgba(15,23,42,.18)",
          "&:hover": { background: "#f8fafc" },
        }}
      >
        Quick actions
        {count ? (
          <span className={styles.badge} style={{ marginLeft: 6 }}>
            {count}
          </span>
        ) : null}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: styles.tokens, sx: { mt: 1, borderRadius: "14px", boxShadow: "0 16px 40px -12px rgba(15,23,42,.35)" } }}
      >
        {items}
      </Menu>
    </>
  );
}

const Hrms = () => {
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(null); // leave | regularize | ask
  const [rev, setRev] = useState(0); // bumps to re-mount (reload) the open tab
  const lastTab = useRef({});

  const load = useCallback(async () => {
    try {
      setMe((await hr("/me")).data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const modes = useMemo(() => (me ? modesFor(me) : []), [me]);
  const requested = params.get("tab");
  const mode = modes.find((m) => m.tabs.includes(requested)) || modes[0];
  const tab = mode ? (mode.tabs.includes(requested) ? requested : mode.tabs[0]) : null;
  if (mode && tab) lastTab.current[mode.key] = tab;

  const goTo = useCallback((key, extra = {}) => setParams({ tab: key, ...extra }), [setParams]);
  const switchMode = (m) => goTo(lastTab.current[m.key] || m.tabs[0]);
  const afterDialog = () => {
    setDialog(null);
    setRev((r) => r + 1);
    load();
  };

  if (!me) {
    return (
      <div className={styles.page}>
        {error ? <div className={styles.errorBox}>{error}</div> : <CircularProgress size={24} />}
      </div>
    );
  }

  const e = me.employee;
  const name = e ? `${e.first_name} ${e.last_name || ""}`.trim() : "";
  const t = me.todayAttendance;
  const tz = me.settings.timezone;
  const Screen = tab && TABS[tab].Component;

  return (
    <div className={`${styles.page} ${mode && mode.key === "hr" ? styles.modeHr : ""}`}>
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          {name ? <Avatar name={name} size={58} className={styles.heroAvatar} /> : null}
          <div style={{ minWidth: 0 }}>
            <div className={styles.heroTitle}>
              {greeting(tz)}
              {name ? `, ${e.first_name}` : ""}
            </div>
            <div className={styles.heroSub}>
              {mode && mode.key === "hr"
                ? me.isHr
                  ? `HR workspace · ${me.settings.company_name}`
                  : "Your team's leave and attendance"
                : [e && e.designation, e && e.employee_code, e && e.manager && `Reports to ${e.manager.name}`].filter(Boolean).join(" · ")}
            </div>
            <div className={styles.heroChips}>
              {e && (
                <span className={styles.heroChip}>
                  <AccessTimeRoundedIcon />
                  {!t || !t.check_in ? "Not checked in" : t.check_out ? `Done · ${fmtTime(t.check_in, tz)} – ${fmtTime(t.check_out, tz)}` : `In since ${fmtTime(t.check_in, tz)}`}
                </span>
              )}
              {me.pendingApprovals > 0 && (
                <span className={styles.heroChip} role="button" style={{ cursor: "pointer" }} onClick={() => goTo("approvals")}>
                  <FactCheckOutlinedIcon />
                  {me.pendingApprovals} waiting for approval
                </span>
              )}
              {me.role !== "employee" && <span className={styles.heroChip}>{ROLE_LABELS[me.role]}</span>}
            </div>
          </div>
        </div>
        <div className={styles.heroRight}>
          {modes.length > 1 && (
            <div className={styles.toggle} role="tablist" aria-label="Workspace">
              {modes.map((m) => {
                const pending = m.key === "hr" ? (me.pendingApprovals || 0) + (me.isHr ? me.openHrRequests || 0 : 0) : 0;
                return (
                  <button key={m.key} type="button" role="tab" aria-selected={mode.key === m.key} className={mode.key === m.key ? styles.toggleOn : ""} onClick={() => switchMode(m)}>
                    {m.icon}
                    {m.label}
                    {pending && mode.key !== m.key ? <span className={styles.badge}>{pending}</span> : null}
                  </button>
                );
              })}
            </div>
          )}
          {modes.length > 0 && <QuickActions me={me} mode={mode && mode.key} goTo={goTo} openDialog={setDialog} onChanged={load} />}
        </div>
      </div>

      {mode ? (
        <nav className={styles.tabs} aria-label={mode.label}>
          {mode.tabs.map((key) => {
            const def = TABS[key];
            const badge = badgeFor(key, me);
            return (
              <button key={key} type="button" className={`${styles.tab} ${tab === key ? styles.tabOn : ""}`} aria-current={tab === key ? "page" : undefined} onClick={() => goTo(key)}>
                {def.icon}
                {key === "team" && !me.isHr ? "My team" : def.label}
                {badge ? <span className={styles.badge}>{badge}</span> : null}
              </button>
            );
          })}
        </nav>
      ) : (
        <div style={{ height: 18 }} />
      )}

      {!mode && (
        <Card>
          <Empty icon={<BadgeOutlinedIcon />} title="No employee profile yet" text="Self-service (check-in, leave, payslips) starts once HR adds you as an employee with your portal email address." />
        </Card>
      )}
      {mode && mode.key === "hr" && me.isHr && !e && (
        <div className={styles.infoBox}>You have HR access but no employee profile of your own, so “My HR” is hidden. Add yourself in Employees with your portal email to use self-service.</div>
      )}
      {Screen && <Screen key={`${tab}-${rev}`} me={me} onChanged={load} goTo={goTo} openDialog={setDialog} />}

      {e && (
        <>
          <ApplyLeaveDialog open={dialog === "leave"} onClose={() => setDialog(null)} onDone={afterDialog} />
          <RegularizeDialog me={me} open={dialog === "regularize"} onClose={() => setDialog(null)} onDone={afterDialog} />
          <AskHrDialog open={dialog === "ask"} onClose={() => setDialog(null)} onDone={afterDialog} />
        </>
      )}
    </div>
  );
};

export default Hrms;
